import { apiFetch, v1Path } from './api';

/** A business requirement every new initiative gets as a launch step in the Business requirements workspace. */
export type TemplateRequirement = { id: string; title: string; description: string | null };

/** A stream every new initiative starts with; `stream` is matched to a stream's name or identifier. */
export type TemplateStream = { stream: string; tasks: string[] };

export type InitiativeTemplate = {
  requirements: TemplateRequirement[];
  streams: TemplateStream[];
  version: number;
  is_default: boolean;
  updated_at: string | null;
};

/** Owner and go-live date for a requirement, chosen on the New initiative page. */
export type RequirementPlan = { owner_ids: string[]; due_date: string | null };

export function fetchInitiativeTemplate(): Promise<InitiativeTemplate> {
  return apiFetch<InitiativeTemplate>(v1Path('/initiatives/template'));
}

export function saveInitiativeTemplate(
  version: number,
  requirements: TemplateRequirement[],
  streams: TemplateStream[],
): Promise<InitiativeTemplate> {
  return apiFetch<InitiativeTemplate>(v1Path('/initiatives/template'), {
    method: 'PUT',
    body: JSON.stringify({ version, requirements, streams }),
  });
}

/** Adds a business requirement to one initiative; the org template is unchanged. */
export function addInitiativeRequirement(
  initiativeId: string,
  fields: { title: string; description?: string },
): Promise<{ id: string; key: string }> {
  return apiFetch<{ id: string; key: string }>(v1Path(`/initiatives/${initiativeId}/requirements`), {
    method: 'POST',
    body: JSON.stringify(fields),
  });
}

type RequirementTask = { id: string; title: string; version: number; project_id: string };

/**
 * Puts the owners and go-live dates chosen on the New initiative page onto the requirement tasks the
 * server created with the initiative. Returns how many requirements there are and which could not be saved.
 */
export async function planRequirements(
  initiativeId: string,
  workspaceId: string,
  requirements: TemplateRequirement[],
  plans: Record<string, RequirementPlan>,
): Promise<{ count: number; failed: string[] }> {
  const res = await apiFetch<{ items: RequirementTask[] }>(
    `${v1Path('/work-items')}?initiative_ids=${initiativeId}&show_sub_items=false&limit=100&offset=0`,
  );
  const tasks = res.items.filter((item) => item.project_id === workspaceId);
  const failed: string[] = [];
  for (const entry of requirements) {
    const plan = plans[entry.id];
    if (!plan || (plan.owner_ids.length === 0 && !plan.due_date)) continue;
    const task = tasks.find((item) => item.title.trim().toLowerCase() === entry.title.trim().toLowerCase());
    if (!task) {
      failed.push(entry.title);
      continue;
    }
    try {
      await apiFetch(v1Path(`/projects/${workspaceId}/work-items/${task.id}`), {
        method: 'PATCH',
        body: JSON.stringify({
          version: task.version,
          ...(plan.owner_ids.length ? { assignee_ids: plan.owner_ids } : {}),
          ...(plan.due_date ? { due_date: plan.due_date } : {}),
        }),
      });
    } catch {
      failed.push(entry.title);
    }
  }
  return { count: tasks.length, failed };
}

type StreamRef = { id: string; name: string; identifier: string };

/**
 * The template's streams as plans for a new initiative, matched to live streams by name or
 * identifier. `missing` lists entries with no matching stream, so the page can say they were left out.
 */
export function plansFromTemplate(
  streams: TemplateStream[],
  projects: StreamRef[],
): { plans: Array<{ project_id: string; tasks: Array<{ title: string; due_date: string | null }> }>; missing: TemplateStream[] } {
  const plans: Array<{ project_id: string; tasks: Array<{ title: string; due_date: string | null }> }> = [];
  const missing: TemplateStream[] = [];
  for (const entry of streams) {
    const key = entry.stream.trim().toLowerCase();
    const project = projects.find((p) => p.name.toLowerCase() === key || p.identifier.toLowerCase() === key);
    if (!project || plans.some((plan) => plan.project_id === project.id)) {
      if (!project) missing.push(entry);
      continue;
    }
    plans.push({ project_id: project.id, tasks: entry.tasks.map((title) => ({ title, due_date: null })) });
  }
  return { plans, missing };
}

/** First problem that would stop the server accepting the default streams, or null. */
export function streamsProblem(streams: TemplateStream[]): string | null {
  const names = new Set<string>();
  for (const entry of streams) {
    const name = entry.stream.trim();
    if (!name) return 'Pick a stream for every default stream.';
    if (names.has(name.toLowerCase())) return `${name} is listed twice.`;
    names.add(name.toLowerCase());
    if (!entry.tasks.some((task) => task.trim())) return `${name} needs at least one task.`;
  }
  return null;
}

export function cleanStreams(streams: TemplateStream[]): TemplateStream[] {
  return streams.map((entry) => ({
    stream: entry.stream.trim(),
    tasks: entry.tasks.map((task) => task.trim()).filter(Boolean),
  }));
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
}

/** A readable id that is not in `taken`, for a new requirement. */
export function newTemplateId(label: string, taken: Set<string>): string {
  const base = slug(label) || 'requirement';
  let id = base;
  for (let n = 2; taken.has(id); n += 1) id = `${base}_${n}`;
  return id;
}

/** Gives requirements added since the last save an id from their title; saved ids never change. */
export function nameNewIds(requirements: TemplateRequirement[], savedIds: Set<string>): TemplateRequirement[] {
  const taken = new Set(savedIds);
  return requirements.map((entry) => {
    if (savedIds.has(entry.id)) return entry;
    const id = newTemplateId(entry.title, taken);
    taken.add(id);
    return { ...entry, id };
  });
}

/** First problem that would stop the server accepting the requirements, or null. */
export function requirementsProblem(requirements: TemplateRequirement[]): string | null {
  const titles = new Set<string>();
  for (const entry of requirements) {
    const title = entry.title.trim();
    if (!title) return 'Every requirement needs a title.';
    if (titles.has(title.toLowerCase())) return `${title} is listed twice.`;
    titles.add(title.toLowerCase());
  }
  return null;
}

/** Requirements in the template's order, matched by title; ones not in the template follow by key. */
export function inTemplateOrder<T extends { title: string; key: string }>(rows: T[], titles: string[]): T[] {
  const position = new Map(titles.map((title, index) => [title.trim().toLowerCase(), index]));
  const rank = (row: T) => position.get(row.title.trim().toLowerCase()) ?? titles.length;
  return [...rows].sort(
    (left, right) => rank(left) - rank(right) || left.key.localeCompare(right.key, undefined, { numeric: true }),
  );
}

export function cleanRequirements(requirements: TemplateRequirement[]): TemplateRequirement[] {
  return requirements.map((entry) => ({
    ...entry,
    title: entry.title.trim(),
    description: entry.description?.trim() || null,
  }));
}
